import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  Building,
  Calendar,
  PlusCircle,
  ArrowsClockwise,
  CheckCircle,
  Warning,
  Trash,
  CaretRight,
  Money,
} from '@phosphor-icons/react';
import { loteGeneralAPI, asignacionesAPI, lotesAPI, expensesAPI } from '../lib/api';
import { formatCurrency, formatDate, CATEGORIAS } from '../lib/utils';

const MESES = [
  { value: 1, label: 'Enero' },
  { value: 2, label: 'Febrero' },
  { value: 3, label: 'Marzo' },
  { value: 4, label: 'Abril' },
  { value: 5, label: 'Mayo' },
  { value: 6, label: 'Junio' },
  { value: 7, label: 'Julio' },
  { value: 8, label: 'Agosto' },
  { value: 9, label: 'Septiembre' },
  { value: 10, label: 'Octubre' },
  { value: 11, label: 'Noviembre' },
  { value: 12, label: 'Diciembre' },
];

// Categorías permitidas para Lote General (sin alimento)
const CATEGORIAS_GENERAL = CATEGORIAS.filter(c => c.value !== 'alimento');

export default function LoteGeneral() {
  const [loteGeneral, setLoteGeneral] = useState(null);
  const [gastos, setGastos] = useState([]);
  const [lotes, setLotes] = useState([]);
  const [resumenMensual, setResumenMensual] = useState([]);
  const [asignaciones, setAsignaciones] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [añoSeleccionado, setAñoSeleccionado] = useState(new Date().getFullYear());
  const [mesSeleccionado, setMesSeleccionado] = useState(null);
  
  // Modal de asignación
  const [showAsignacionModal, setShowAsignacionModal] = useState(false);
  const [mesParaAsignar, setMesParaAsignar] = useState(null);
  const [lotesSeleccionados, setLotesSeleccionados] = useState([]);
  
  // Modal de distribución
  const [showDistribucionModal, setShowDistribucionModal] = useState(false);
  const [mesParaDistribuir, setMesParaDistribuir] = useState(null);

  useEffect(() => {
    loadData();
  }, [añoSeleccionado]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Obtener o crear el Lote General
      const loteRes = await loteGeneralAPI.get();
      setLoteGeneral(loteRes.data);
      
      // Obtener gastos del lote general
      const gastosRes = await loteGeneralAPI.getGastos();
      setGastos(gastosRes.data);
      
      // Obtener todos los lotes (excepto el general)
      const lotesRes = await lotesAPI.getAll();
      setLotes(lotesRes.data.filter(l => !l.es_general));
      
      // Obtener resumen mensual
      const resumenRes = await loteGeneralAPI.getResumenMensual(añoSeleccionado);
      setResumenMensual(resumenRes.data);
      
      // Obtener asignaciones
      const asignacionesRes = await asignacionesAPI.getAll(añoSeleccionado);
      setAsignaciones(asignacionesRes.data);
      
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  // Gastos filtrados por mes
  const gastosFiltrados = useMemo(() => {
    if (!mesSeleccionado) return gastos;
    
    return gastos.filter(g => {
      if (!g.fecha) return false;
      const fecha = new Date(g.fecha);
      return fecha.getMonth() + 1 === mesSeleccionado && fecha.getFullYear() === añoSeleccionado;
    });
  }, [gastos, mesSeleccionado, añoSeleccionado]);

  // Total de gastos filtrados
  const totalGastosFiltrados = useMemo(() => {
    return gastosFiltrados.reduce((sum, g) => sum + (g.total_dop || g.total || 0), 0);
  }, [gastosFiltrados]);

  // Obtener asignación de un mes
  const getAsignacionMes = (mes) => {
    return asignaciones.find(a => a.mes === mes) || { lote_ids: [], lotes_nombres: [] };
  };

  // Abrir modal de asignación
  const abrirAsignacion = (mes) => {
    const asignacion = getAsignacionMes(mes);
    setMesParaAsignar(mes);
    setLotesSeleccionados(asignacion.lote_ids || []);
    setShowAsignacionModal(true);
  };

  // Guardar asignación
  const guardarAsignacion = async () => {
    try {
      await asignacionesAPI.guardar(añoSeleccionado, mesParaAsignar, lotesSeleccionados);
      toast.success(`Asignación de ${MESES.find(m => m.value === mesParaAsignar)?.label} guardada`);
      setShowAsignacionModal(false);
      loadData();
    } catch (error) {
      toast.error('Error al guardar asignación');
    }
  };

  // Abrir modal de distribución
  const abrirDistribucion = (mes) => {
    const asignacion = getAsignacionMes(mes);
    if (!asignacion.lote_ids || asignacion.lote_ids.length === 0) {
      toast.error('Primero debe asignar lotes a este mes');
      return;
    }
    setMesParaDistribuir(mes);
    setShowDistribucionModal(true);
  };

  // Ejecutar distribución
  const ejecutarDistribucion = async () => {
    try {
      const result = await loteGeneralAPI.distribuir(añoSeleccionado, mesParaDistribuir);
      toast.success(
        `Distribución completada: ${formatCurrency(result.data.monto_por_lote)} por lote (${result.data.cantidad_lotes} lotes)`
      );
      setShowDistribucionModal(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al distribuir gastos');
    }
  };

  // Toggle lote en selección
  const toggleLoteSeleccion = (loteId) => {
    setLotesSeleccionados(prev => 
      prev.includes(loteId)
        ? prev.filter(id => id !== loteId)
        : [...prev, loteId]
    );
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <ArrowsClockwise className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in" data-testid="lote-general-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Building className="text-[#E73425]" />
              Lote General - Gastos Compartidos
            </h1>
            <p className="text-muted-foreground">
              Registre gastos generales y distribúyalos entre los lotes activos
            </p>
          </div>
          
          <div className="flex items-center gap-2">
            <Select value={String(añoSeleccionado)} onValueChange={(v) => setAñoSeleccionado(parseInt(v))}>
              <SelectTrigger className="w-[120px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[2024, 2025, 2026, 2027].map(año => (
                  <SelectItem key={año} value={String(año)}>{año}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Link to={`/gastos/nuevo?lote_general=true`}>
              <Button className="bg-[#E73425] hover:bg-[#c42d20]">
                <PlusCircle size={18} className="mr-2" />
                Nuevo Gasto
              </Button>
            </Link>
          </div>
        </div>

        {/* Matriz de Asignación de Lotes por Mes */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar size={20} />
              Asignación de Lotes por Mes - {añoSeleccionado}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="p-3 text-left font-semibold">Mes</th>
                    <th className="p-3 text-left font-semibold">Lotes Asignados</th>
                    <th className="p-3 text-right font-semibold">Total Gastos</th>
                    <th className="p-3 text-center font-semibold">Estado</th>
                    <th className="p-3 text-center font-semibold">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {MESES.map(mes => {
                    const asignacion = getAsignacionMes(mes.value);
                    const resumen = resumenMensual.find(r => r.mes === mes.value) || { total_gastos: 0, gastos_distribuidos: false };
                    const cantidadLotes = asignacion.lote_ids?.length || 0;
                    const montoPorLote = cantidadLotes > 0 ? resumen.total_gastos / cantidadLotes : 0;
                    
                    return (
                      <tr key={mes.value} className="border-b hover:bg-muted/30">
                        <td className="p-3 font-medium">{mes.label}</td>
                        <td className="p-3">
                          {cantidadLotes > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {asignacion.lotes_nombres?.map((nombre, i) => (
                                <Badge key={i} variant="outline" className="text-xs">
                                  {nombre}
                                </Badge>
                              ))}
                            </div>
                          ) : (
                            <span className="text-muted-foreground text-sm">Sin asignar</span>
                          )}
                        </td>
                        <td className="p-3 text-right">
                          <div>
                            <span className="font-semibold">{formatCurrency(resumen.total_gastos)}</span>
                            {cantidadLotes > 0 && resumen.total_gastos > 0 && (
                              <div className="text-xs text-muted-foreground">
                                {formatCurrency(montoPorLote)} c/u
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="p-3 text-center">
                          {resumen.gastos_distribuidos ? (
                            <Badge className="bg-emerald-100 text-emerald-700">
                              <CheckCircle size={14} className="mr-1" />
                              Distribuido
                            </Badge>
                          ) : resumen.total_gastos > 0 ? (
                            <Badge className="bg-amber-100 text-amber-700">
                              <Warning size={14} className="mr-1" />
                              Pendiente
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-muted-foreground">
                              Sin gastos
                            </Badge>
                          )}
                        </td>
                        <td className="p-3">
                          <div className="flex justify-center gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => abrirAsignacion(mes.value)}
                            >
                              Asignar Lotes
                            </Button>
                            {!resumen.gastos_distribuidos && resumen.total_gastos > 0 && cantidadLotes > 0 && (
                              <Button
                                size="sm"
                                className="bg-[#E73425] hover:bg-[#c42d20]"
                                onClick={() => abrirDistribucion(mes.value)}
                              >
                                Distribuir
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Gastos del Lote General */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Money size={20} />
                Gastos Registrados
              </CardTitle>
              <div className="flex items-center gap-2">
                <Select
                  value={mesSeleccionado ? String(mesSeleccionado) : 'all'}
                  onValueChange={(v) => setMesSeleccionado(v === 'all' ? null : parseInt(v))}
                >
                  <SelectTrigger className="w-[150px]">
                    <SelectValue placeholder="Todos los meses" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos los meses</SelectItem>
                    {MESES.map(mes => (
                      <SelectItem key={mes.value} value={String(mes.value)}>{mes.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {gastosFiltrados.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No hay gastos registrados {mesSeleccionado && `para ${MESES.find(m => m.value === mesSeleccionado)?.label}`}
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-muted/50">
                        <th className="p-3 text-left">Fecha</th>
                        <th className="p-3 text-left">Categoría</th>
                        <th className="p-3 text-left">Descripción</th>
                        <th className="p-3 text-right">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {gastosFiltrados.map(gasto => (
                        <tr key={gasto.id} className="border-b hover:bg-muted/30">
                          <td className="p-3">{formatDate(gasto.fecha)}</td>
                          <td className="p-3">
                            <Badge variant="outline">
                              {CATEGORIAS.find(c => c.value === gasto.categoria)?.label || gasto.categoria}
                            </Badge>
                          </td>
                          <td className="p-3">{gasto.descripcion}</td>
                          <td className="p-3 text-right font-semibold">
                            {formatCurrency(gasto.total_dop || gasto.total)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="bg-muted/50 font-semibold">
                        <td colSpan={3} className="p-3 text-right">Total:</td>
                        <td className="p-3 text-right text-[#E73425]">
                          {formatCurrency(totalGastosFiltrados)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Modal de Asignación de Lotes */}
        <Dialog open={showAsignacionModal} onOpenChange={setShowAsignacionModal}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>
                Asignar Lotes - {MESES.find(m => m.value === mesParaAsignar)?.label} {añoSeleccionado}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <p className="text-sm text-muted-foreground">
                Seleccione los lotes que corresponden a este mes para la distribución de gastos.
              </p>
              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                {lotes.filter(l => l.estado === 'activo' && !l.es_general).map(lote => {
                  const isSelected = lotesSeleccionados.includes(lote.id);
                  return (
                    <div
                      key={lote.id}
                      className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors select-none ${
                        isSelected
                          ? 'bg-[#E73425]/10 border-[#E73425]'
                          : 'hover:bg-muted/50'
                      }`}
                      onClick={() => toggleLoteSeleccion(lote.id)}
                    >
                      <div 
                        className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                          isSelected ? 'bg-[#E73425] border-[#E73425]' : 'border-gray-300'
                        }`}
                      >
                        {isSelected && (
                          <CheckCircle size={14} className="text-white" weight="bold" />
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="font-medium">{lote.nombre}</div>
                        <div className="text-xs text-muted-foreground">
                          {lote.cantidad_pollitos?.toLocaleString()} pollos
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="text-sm text-muted-foreground">
                {lotesSeleccionados.length} lote(s) seleccionado(s)
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAsignacionModal(false)}>
                Cancelar
              </Button>
              <Button onClick={guardarAsignacion} className="bg-[#E73425] hover:bg-[#c42d20]">
                Guardar Asignación
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Modal de Confirmación de Distribución */}
        <Dialog open={showDistribucionModal} onOpenChange={setShowDistribucionModal}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>
                Confirmar Distribución - {MESES.find(m => m.value === mesParaDistribuir)?.label} {añoSeleccionado}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              {(() => {
                const asignacion = getAsignacionMes(mesParaDistribuir);
                const resumen = resumenMensual.find(r => r.mes === mesParaDistribuir) || { total_gastos: 0 };
                const cantidadLotes = asignacion.lote_ids?.length || 0;
                const montoPorLote = cantidadLotes > 0 ? resumen.total_gastos / cantidadLotes : 0;
                
                return (
                  <>
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                      <p className="text-sm text-amber-800">
                        <Warning className="inline mr-2" />
                        Esta acción creará gastos de prorrateo en cada lote asignado. 
                        Una vez distribuido, necesitará código de super usuario para revertir.
                      </p>
                    </div>
                    
                    <div className="space-y-3">
                      <div className="flex justify-between py-2 border-b">
                        <span className="text-muted-foreground">Total a distribuir:</span>
                        <span className="font-bold">{formatCurrency(resumen.total_gastos)}</span>
                      </div>
                      <div className="flex justify-between py-2 border-b">
                        <span className="text-muted-foreground">Lotes asignados:</span>
                        <span className="font-bold">{cantidadLotes}</span>
                      </div>
                      <div className="flex justify-between py-2 border-b">
                        <span className="text-muted-foreground">Monto por lote:</span>
                        <span className="font-bold text-[#E73425]">{formatCurrency(montoPorLote)}</span>
                      </div>
                      <div className="pt-2">
                        <span className="text-muted-foreground text-sm">Lotes que recibirán el gasto:</span>
                        <div className="flex flex-wrap gap-1 mt-2">
                          {asignacion.lotes_nombres?.map((nombre, i) => (
                            <Badge key={i} variant="outline">{nombre}</Badge>
                          ))}
                        </div>
                      </div>
                    </div>
                  </>
                );
              })()}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDistribucionModal(false)}>
                Cancelar
              </Button>
              <Button onClick={ejecutarDistribucion} className="bg-[#E73425] hover:bg-[#c42d20]">
                Confirmar Distribución
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
